/**
 * useContextTree.ts
 *
 * Custom hook for fetching and managing context execution results in tree mode.
 * Provides tree navigation: expand/collapse nodes, lazy-load children with pagination.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import type {
  SphereNode,
  ContextTreeExecutionResult,
  CacheMetadata,
  EnrichmentOverrides,
  RuntimeFilterEnvelope,
} from 'types/context';
import type { UnifiedFilters } from 'types/filters';
import { executeContextTree, loadContextChildren } from '../api/contextTreeApi';
import { isTransientReadFailure } from '../utils/isTransientReadFailure';

/** Parameters for executing a context in tree mode */
export interface UseContextTreeParams {
  /** Context system name to execute */
  systemName: string;
  /** Root sphere ID for the query */
  rootSphereId: string;
  /** Number of children per level for initial load */
  limit?: number;
  /** Max depth for traversal */
  depth?: number;
  /** Enrichment overrides — declare what the consumer surface needs to render */
  enrichmentOverrides?: EnrichmentOverrides;
  /**
   * Runtime filters from UI (additive narrowing on context results). A bare
   * `UnifiedFilters` applies to every level; a `RuntimeFilterEnvelope` (from
   * `useLevelFilters`) additionally scopes clauses per level via `byLevelId`.
   * `runtimeFiltersKey` below re-keys on either shape via `JSON.stringify`,
   * so no extra wiring is needed here for the envelope form.
   */
  runtimeFilters?: RuntimeFilterEnvelope | UnifiedFilters;
  /** Disable lazy loading to eagerly fetch all levels up to depth */
  lazyLoad?: boolean;
  /** View-declared sphere property names to project (date/text columns). */
  columnProperties?: string[];
}

/** Return value of the useContextTree hook */
export interface UseContextTreeReturn {
  /** Root sphere nodes (null until first fetch completes) */
  roots: SphereNode[] | null;
  /** Full execution result metadata */
  data: ContextTreeExecutionResult | null;
  /** Whether initial fetch is in progress */
  loading: boolean;
  /** Error message if the last operation failed */
  error: string | null;
  /** Cache metadata from the last successful fetch */
  cacheMetadata: CacheMetadata | null;
  /** Re-fetch tree with fresh data (bypasses cache) */
  refresh: () => Promise<void>;
  /**
   * Re-fetch the tree in the background after a sphere in it changed (bypasses cache): no loading
   * state; a transient failure keeps the tree on screen, a refusal takes the normal failure path.
   */
  refreshQuietly: () => Promise<void>;
  /** Expand a node by loading its children */
  expandNode: (sphereId: string, level: number, direction?: 'inwards' | 'outwards' | 'both') => Promise<void>;
  /** Collapse a node (clear its children from state) */
  collapseNode: (sphereId: string) => void;
  /** Check if a node has more children to load */
  hasMore: (sphereId: string) => boolean;
  /** Load next page of children for a node */
  loadMore: (sphereId: string, level: number) => Promise<void>;
  /** Expanded node keys */
  expandedKeys: Record<string, boolean>;
}

/**
 * Deep-clone and replace a node in a SphereNode tree by ID.
 * Returns a new tree with the target node replaced.
 */
function replaceNodeById(
  nodes: SphereNode[],
  targetId: string,
  updater: (node: SphereNode) => SphereNode
): SphereNode[] {
  return nodes.map((node) => {
    if (node.id === targetId) {
      return updater(node);
    }
    if (node.children.length > 0) {
      return { ...node, children: replaceNodeById(node.children, targetId, updater) };
    }
    return node;
  });
}

/**
 * Find a node by ID in a SphereNode tree.
 */
function findNodeById(nodes: SphereNode[], targetId: string): SphereNode | undefined {
  for (const node of nodes) {
    if (node.id === targetId) return node;
    if (node.children.length > 0) {
      const found = findNodeById(node.children, targetId);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Hook for consuming context execution results in tree mode from graph-service.
 * Provides expand/collapse navigation and lazy-load pagination for children.
 *
 * @param apiClients - API clients from ApiClientsContext (pass null if not ready)
 * @param params - Execution parameters (systemName, rootSphereId, etc.)
 */
export function useContextTree(
  apiClients: ApiClients | null,
  params: UseContextTreeParams
): UseContextTreeReturn {
  const { systemName, rootSphereId, limit = 50, depth, lazyLoad, enrichmentOverrides, runtimeFilters, columnProperties } = params;

  // Stable serialization for dependency arrays to avoid infinite re-renders
  const runtimeFiltersKey = useMemo(
    () => (runtimeFilters ? JSON.stringify(runtimeFilters) : ''),
    [runtimeFilters]
  );

  const enrichmentKey = useMemo(
    () => (enrichmentOverrides ? JSON.stringify(enrichmentOverrides) : ''),
    [enrichmentOverrides]
  );

  const columnPropertiesKey = useMemo(
    () => (columnProperties?.length ? columnProperties.join(',') : ''),
    [columnProperties]
  );

  const [data, setData] = useState<ContextTreeExecutionResult | null>(null);
  // Start in the loading state whenever a tree is going to be fetched. Starting at
  // `false` made consumers briefly believe the (empty) tree had loaded — the armillary
  // loader would start fading out and then snap back once the real fetch flipped this
  // to true on the next render.
  const [loading, setLoading] = useState<boolean>(Boolean(systemName && rootSphereId));
  const [error, setError] = useState<string | null>(null);
  const [cacheMetadata, setCacheMetadata] = useState<CacheMetadata | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  const isMountedRef = useRef(true);
  // Monotonic id for the in-flight request. Navigating between spheres (e.g. clicking
  // through Atlas rows) starts a new fetch before the previous one lands; without this
  // an older, slower response overwrites a newer one and the view + breadcrumb snap
  // back to a sphere the user already navigated away from.
  const fetchSeqRef = useRef(0);
  // The (context, sphere) pair the data we're holding describes.
  const loadedKeyRef = useRef<string | null>(null);
  // Whether the tree on screen came from a read that succeeded. A normal read clears it: the tree it
  // replaces no longer answers what was asked for (another sphere, other filters).
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTree = useCallback(
    async (options: { skipCache?: boolean; quiet?: boolean } = {}) => {
      const { skipCache = false } = options;

      if (!apiClients || !systemName || !rootSphereId) return;

      // With nothing loaded there is no tree to keep, so a quiet read behaves as a normal one: it may
      // have superseded the first load, and its failure has to show the error state, not an empty view.
      const quiet = (options.quiet ?? false) && hasLoadedRef.current;

      const seq = ++fetchSeqRef.current;
      const key = `${systemName}::${rootSphereId}`;

      // Drop data belonging to a different sphere/context so consumers never render
      // the previous sphere's root while the new one is in flight. A same-key refetch
      // (refresh/skipCache) keeps what's on screen.
      if (loadedKeyRef.current !== key) {
        loadedKeyRef.current = key;
        setData(null);
        setCacheMetadata(null);
      }

      if (!quiet) {
        setLoading(true);
        setError(null);
        hasLoadedRef.current = false;
      }

      try {
        const response = await executeContextTree(apiClients.graphClient, systemName, {
          rootSphereId,
          limit,
          depth,
          skipCache: skipCache || undefined,
          lazyLoad,
          enrichmentOverrides,
          runtimeFilters,
          columnProperties,
        });

        if (!isMountedRef.current || seq !== fetchSeqRef.current) return;

        setData(response);
        setCacheMetadata(response._cache ?? null);
        setExpandedKeys({});
        hasLoadedRef.current = true;
        if (quiet) setError(null);
      } catch (err: unknown) {
        if (!isMountedRef.current || seq !== fetchSeqRef.current) return;
        const message = err instanceof Error ? err.message : 'Failed to execute context tree';
        // A transient failure keeps the tree; a refusal takes the normal failure path (D71).
        if (quiet && isTransientReadFailure(err)) {
          console.error('[useContextTree] Background refresh failed; keeping the tree on screen:', message);
          return;
        }
        hasLoadedRef.current = false;
        setError(message);
        setData(null);
      } finally {
        if (isMountedRef.current && seq === fetchSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [apiClients, systemName, rootSphereId, limit, depth, lazyLoad, enrichmentKey, runtimeFiltersKey, columnPropertiesKey]
  );

  // Initial fetch when params are ready (or when filters change)
  useEffect(() => {
    if (apiClients && systemName && rootSphereId) {
      fetchTree();
    }
  }, [apiClients, systemName, rootSphereId, limit, depth, lazyLoad, enrichmentKey, runtimeFiltersKey, columnPropertiesKey, fetchTree]);

  const refresh = useCallback(async () => {
    await fetchTree({ skipCache: true });
  }, [fetchTree]);

  const refreshQuietly = useCallback(async () => {
    await fetchTree({ skipCache: true, quiet: true });
  }, [fetchTree]);

  const expandNode = useCallback(
    async (sphereId: string, level: number, direction?: 'inwards' | 'outwards' | 'both') => {
      if (!apiClients || !data) return;

      setError(null);

      try {
        const result = await loadContextChildren(apiClients.graphClient, systemName, {
          parentSphereId: sphereId,
          level,
          rootSphereId,
          limit,
          enrichmentOverrides,
          runtimeFilters,
          columnProperties,
          direction,
        });

        if (!isMountedRef.current) return;

        setData((prev) => {
          if (!prev) return prev;
          const updatedRoots = replaceNodeById(prev.roots, sphereId, (node) => ({
            ...node,
            children: result.children,
            // Keep the node's own count badge in sync with the child total just loaded.
            // A node's edgeCount is normally computed by its parent (with the parent
            // level's direction); when this node is re-expanded with a direction override
            // (the metaspace direction pill), the parent-computed badge is stale, so we
            // refresh it from the freshly-loaded, direction-correct total.
            ...(result.pagination.totalCount !== undefined && {
              edgeCount: result.pagination.totalCount,
            }),
            childPagination: {
              hasMore: result.pagination.hasMore,
              nextToken: result.pagination.nextToken,
              totalCount: result.pagination.totalCount,
            },
          }));
          return { ...prev, roots: updatedRoots };
        });

        setExpandedKeys((prev) => ({ ...prev, [sphereId]: true }));
      } catch (err: unknown) {
        if (!isMountedRef.current) return;
        const message = err instanceof Error ? err.message : 'Failed to load children';
        setError(message);
      }
    },
    [apiClients, data, systemName, rootSphereId, limit, enrichmentKey, runtimeFiltersKey, columnPropertiesKey]
  );

  const collapseNode = useCallback(
    (sphereId: string) => {
      if (!data) return;

      setData((prev) => {
        if (!prev) return prev;
        const updatedRoots = replaceNodeById(prev.roots, sphereId, (node) => ({
          ...node,
          children: [],
          childPagination: undefined,
        }));
        return { ...prev, roots: updatedRoots };
      });

      setExpandedKeys((prev) => {
        const next = { ...prev };
        delete next[sphereId];
        return next;
      });
    },
    [data]
  );

  const hasMoreFn = useCallback(
    (sphereId: string): boolean => {
      if (!data) return false;
      const node = findNodeById(data.roots, sphereId);
      return node?.childPagination?.hasMore ?? false;
    },
    [data]
  );

  const loadMore = useCallback(
    async (sphereId: string, level: number) => {
      if (!apiClients || !data) return;

      const node = findNodeById(data.roots, sphereId);
      if (!node?.childPagination?.hasMore) return;

      setError(null);

      try {
        const result = await loadContextChildren(apiClients.graphClient, systemName, {
          parentSphereId: sphereId,
          level,
          rootSphereId,
          limit,
          token: node.childPagination.nextToken,
          enrichmentOverrides,
          runtimeFilters,
          columnProperties,
        });

        if (!isMountedRef.current) return;

        // No-progress detection (computed against the node we paginated from).
        // A page that adds zero NEW children means pagination has stalled — the
        // backend returned hasMore:true with a non-advancing token, an
        // all-duplicate page, or a totalCount that over-counts a filtered result.
        const priorIds = new Set(node.children.map((c) => c.id));
        const madeProgress = result.children.some((c) => !priorIds.has(c.id));
        if (!madeProgress) {
          // Left unchecked this re-arms the infinite-scroll observer forever,
          // spamming identical children?parentSphereId requests. Stop here.
          console.warn(
            `[useContextTree] loadMore for parent ${sphereId} returned no new children ` +
              `(backend hasMore=${result.pagination.hasMore}); halting pagination to prevent a runaway. ` +
              `Refresh to retry from a clean slate.`
          );
        }

        setData((prev) => {
          if (!prev) return prev;
          const updatedRoots = replaceNodeById(prev.roots, sphereId, (n) => {
            const existingIds = new Set(n.children.map((c) => c.id));
            const newChildren = result.children.filter((c) => !existingIds.has(c.id));
            return {
              ...n,
              children: [...n.children, ...newChildren],
              childPagination: {
                // Terminate on no-progress regardless of what the backend says:
                // no new rows ⇒ pagination is exhausted for our purposes, so the
                // infinite-scroll sentinel must disarm.
                hasMore: newChildren.length > 0 ? result.pagination.hasMore : false,
                nextToken: result.pagination.nextToken,
                totalCount: result.pagination.totalCount,
              },
            };
          });
          return { ...prev, roots: updatedRoots };
        });
      } catch (err: unknown) {
        if (!isMountedRef.current) return;
        const message = err instanceof Error ? err.message : 'Failed to load more children';
        setError(message);
      }
    },
    [apiClients, data, systemName, rootSphereId, limit, enrichmentKey, runtimeFiltersKey, columnPropertiesKey]
  );

  return {
    roots: data?.roots ?? null,
    data,
    loading,
    error,
    cacheMetadata,
    refresh,
    refreshQuietly,
    expandNode,
    collapseNode,
    hasMore: hasMoreFn,
    loadMore,
    expandedKeys,
  };
}
